import { Body, Tooltip } from '@assembly-js/design-system';
import { IconButton, Menu, MenuItem, MenuProps } from '@material-ui/core';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

import { PlusIcon } from 'src/components/icons';
import { BaseActionsMenuProps } from 'src/constants';
import RowDivider from 'src/legacy/components/RowDivider';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import { BlackHeadings, red } from 'src/theme/colors';
import {
  extraWideMenuMinWidth,
  regularMenuMinWidth,
  skinnyMenuMinWidth,
  wideMenuMinWidth,
} from 'src/theme/dimensions';

import { ActionMenuItem } from './ActionMenuItem';
import { Action } from './BaseActionsMenu';
import { BaseMenuInput } from './BaseMenuInput';

export interface MenuAction {
  key: string;
  name: string;
  onClick?: (params?: any) => void;
  icon?: React.ReactElement;
  value?: string;
  isDelete?: boolean;
}

const useStyles = makeStyles()((theme) => ({
  skinny: {
    minWidth: skinnyMenuMinWidth,
    marginTop: theme.spacing(0.5),
  },
  regular: {
    minWidth: regularMenuMinWidth,
    marginTop: theme.spacing(0.5),
  },
  wide: {
    minWidth: wideMenuMinWidth,
    marginTop: theme.spacing(0.5),
  },
  extraWide: {
    minWidth: extraWideMenuMinWidth,
    marginTop: theme.spacing(0.5),
  },
  menuTitle: {
    color: BlackHeadings,
    '&:hover': {
      background: 'white',
    },
  },
  addActionItem: {
    color: BlackHeadings,
  },
  customFieldEdit: {
    width: 120,
  },
  delete: {
    color: red,
    '& svg': {
      color: red,
    },
  },
  icon: {
    color: BlackHeadings,
    padding: 0,
    marginRight: theme.spacing(2),
    '& svg': {
      fontSize: 14,
    },
  },
}));

export const BaseMenu: React.FC<BaseActionsMenuProps> = ({
  variant = 'regular',
  menuTitle,
  menuInputProps,
  addOptionLabel,
  onAddOption,
  headerActions,
  actions,
  menuProps,
  showTitleDivider = true,
  preventClickEventPropagation,
}) => {
  const { classes } = useStyles();

  const onClickMenuItem = (
    event: React.MouseEvent<HTMLLIElement>,
    action: Action,
  ) => {
    // Prevent the event propagation if parent element get affected by subsequent DOM child click.
    if (preventClickEventPropagation) {
      event.stopPropagation();
    }
    // Event propagation allowed to enable click on parent component and thus removing active state from parent component
    // This passes the id of the menu item so that it can be used in the menu item action handler.
    // e.g. Delete company menu action needs to know what the companyId to delete is.
    action.onClick({
      ...event,
      currentTarget: {
        ...event.currentTarget,
        id: menuProps?.id || '',
      },
    });
    if (menuProps?.onClose) {
      menuProps.onClose(event, 'backdropClick');
    }
  };

  if (!actions) {
    return <div />;
  }

  return (
    <div>
      <Menu
        {...(menuProps as MenuProps)}
        classes={{ paper: classes[variant as keyof typeof classes] }}
      >
        {menuInputProps && <BaseMenuInput {...menuInputProps} />}

        {menuTitle && (
          <div>
            <MenuItem classes={{ root: classes.menuTitle }}>
              <BaseTypography fontType="15Regular">{menuTitle}</BaseTypography>
            </MenuItem>
            {actions.length > 0 && showTitleDivider && (
              <RowDivider mt={0.5} mb={0.5} />
            )}
          </div>
        )}

        {headerActions && headerActions.length > 0 && (
          <div>
            {headerActions.map((action) => (
              <div key={action.key || action.name}>
                {action.tooltipLabel && action.isDisabled ? (
                  <Tooltip
                    content={
                      action.tooltipLabel ? (
                        <Body size="sm">{action.tooltipLabel}</Body>
                      ) : null
                    }
                    position="left"
                  >
                    <div>
                      <ActionMenuItem
                        action={action}
                        onClick={onClickMenuItem}
                      />
                    </div>
                  </Tooltip>
                ) : (
                  <ActionMenuItem action={action} onClick={onClickMenuItem} />
                )}
              </div>
            ))}
            <RowDivider mt={0.5} mb={0.5} />
          </div>
        )}
        {actions.map((action) => (
          <div key={action.key || action.name}>
            {action.tooltipLabel && action.isDisabled ? (
              <Tooltip
                content={
                  action.tooltipLabel ? (
                    <Body size="sm">{action.tooltipLabel}</Body>
                  ) : null
                }
                position="left"
              >
                <div>
                  <ActionMenuItem action={action} onClick={onClickMenuItem} />
                  {action.hasDivider && <RowDivider mt={0.5} mb={0.5} />}
                </div>
              </Tooltip>
            ) : (
              <ActionMenuItem action={action} onClick={onClickMenuItem} />
            )}
            {action.hasDivider &&
              !(action.tooltipLabel && action.isDisabled) && (
                <RowDivider mt={0.5} mb={0.5} />
              )}
          </div>
        ))}
        {addOptionLabel && (
          <div>
            <RowDivider mt={0.5} mb={0.5} />
            <MenuItem
              classes={{ root: classes.addActionItem }}
              onClick={onAddOption}
            >
              <IconButton className={classes.icon}>
                <PlusIcon />
              </IconButton>
              <BaseTypography>{addOptionLabel}</BaseTypography>
            </MenuItem>
          </div>
        )}
      </Menu>
    </div>
  );
};
